import { useEffect, useState } from 'react';
import { apiFetch } from '../services/api';

/**
 * Debounced customer autocomplete.
 *
 * Replaces the `customers.filter(c => c.name.includes(q))` pattern that
 * used to live in every picker modal — the client no longer needs the
 * full tenant customers array for search.
 *
 * Hits GET /api/analytics/customers-search which returns up to `limit`
 * matches ordered by name (ILIKE on name, digits-substring on phone).
 * Empty query returns []; callers decide whether to still open their
 * dropdown with "start typing to search" hint.
 *
 * @param {string} query      - raw user input
 * @param {object} [opts]
 * @param {number} [opts.limit=30]  max results (server caps at 200)
 * @param {number} [opts.delayMs=200]  debounce window
 * @param {boolean} [opts.enabled=true] pause fetches (e.g. when dropdown closed)
 * @param {boolean} [opts.browseOnEmpty=false] порожній запит теж робить
 *        запит і повертає перших `limit` за абеткою. Ендпоінт це вміє від
 *        початку — просто гортати список зручніше, ніж вгадувати перші
 *        літери, коли не памʼятаєш точного написання прізвища.
 * @returns {{ results: Array, loading: boolean }}
 */
export function useCustomerSearch(query, opts = {}) {
  const { limit = 30, delayMs = 200, enabled = true, browseOnEmpty = false } = opts;
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const q = (query || '').trim();
    if (!q && !browseOnEmpty) { setResults([]); setLoading(false); return; }

    setLoading(true);
    let cancelled = false;
    const t = setTimeout(() => {
      apiFetch(`/api/analytics/customers-search?q=${encodeURIComponent(q)}&limit=${limit}`)
        .then(r => { if (!cancelled) setResults(r?.customers || []); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, delayMs);

    return () => { cancelled = true; clearTimeout(t); };
  }, [query, limit, delayMs, enabled, browseOnEmpty]);

  return { results, loading };
}
